import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface ActionTooltipProps {
  children: React.ReactNode;
  title: string;
  className?: string;
  placement?: 'top' | 'bottom';
}

/**
 * Hover tooltip that renders into a body-level portal with fixed positioning,
 * so it is never clipped by an ancestor's `overflow: hidden/auto` (e.g. cards
 * or scrollable table wrappers).
 */
const ActionTooltip: React.FC<ActionTooltipProps> = ({ children, title, className = '', placement = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);

  const show = useCallback(() => {
    const el = ref.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setPos({ top: placement === 'bottom' ? r.bottom : r.top, left: r.left + r.width / 2 });
    }
    setVisible(true);
  }, [placement]);

  return (
    <div
      ref={ref}
      className={`relative inline-block ${className}`}
      onMouseEnter={show}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && createPortal(
        <div
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            transform:
              placement === 'bottom'
                ? 'translate(-50%, 8px)'
                : 'translate(-50%, calc(-100% - 8px))',
            zIndex: 99999,
          }}
          className="px-3 py-2 text-sm text-white bg-gray-800 rounded whitespace-nowrap shadow-lg pointer-events-none"
        >
          {title}
          {/* Arrow */}
          {placement === 'bottom' ? (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2
                       border-4 border-transparent border-b-gray-800"></div>
          ) : (
            <div className="absolute top-full left-1/2 transform -translate-x-1/2
                       border-4 border-transparent border-t-gray-800"></div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionTooltip;
